import { useState, useEffect } from "react";
import EmployeeLayout from "../../layouts/EmployeeLayout";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  getEmployeeWorkAssignments,
  getAssignedProductivity,
} from "../../api/employeeService";
import { getAttendance } from "../../api/attendanceService";
import * as XLSX from "xlsx";
import useAuthStore from "../../store/authStore";

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatDuration = (decimalHrs) => {
  if (decimalHrs === "" || decimalHrs === null || decimalHrs === undefined) return "--";
  const num = parseFloat(decimalHrs);
  if (isNaN(num) || num <= 0) return "--";
  const h = Math.floor(num);
  const m = Math.round((num - h) * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const displayVal = (val) => {
  if (val === null || val === undefined || String(val).trim() === "") return "--";
  return val;
};

const monthDict = {
  January: 1, February: 2, March: 3, April: 4,
  May: 5, June: 6, July: 7, August: 8,
  September: 9, October: 10, November: 11, December: 12,
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function EmployeeProjectProgress({ employeeId: propEmployeeId, embedMode = false }) {
  const navigate = useNavigate();
  const loggedUserId = useAuthStore((state) => state.employeeId);
  const targetEmployeeId = propEmployeeId || loggedUserId;

  const [month, setMonth] = useState(new Date().toLocaleString("default", { month: "long" }));
  const [year, setYear] = useState(new Date().getFullYear());
  const [activities, setActivities] = useState([]);
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [loading, setLoading] = useState(true);
  const [attendanceHrs, setAttendanceHrs] = useState(0);
  const [assignedProductivityHrs, setAssignedProductivityHrs] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const numericMonth = monthDict[month] || new Date().getMonth() + 1;
        const targetYear = year || new Date().getFullYear();

        const queryParams = { month: numericMonth, year: targetYear };
        if (targetEmployeeId && targetEmployeeId !== loggedUserId) {
          queryParams.employee_id = targetEmployeeId;
        }

        // Fetch work assignments for the month
        const [actRes, attendanceRes, assignedRes] = await Promise.all([
          getEmployeeWorkAssignments(queryParams),
          getAttendance(queryParams),
          getAssignedProductivity(queryParams),
        ]);

        // Activities
        const acts = actRes.data || [];
        setActivities(acts);

        // Compute available years from activities
        const yearsFromActs = [...new Set(acts.map(a => {
          if (!a.activity_date) return null;
          return parseInt(a.activity_date.split("-")[0], 10);
        }).filter(Boolean))];
        const currentYear = new Date().getFullYear();
        const allYears = Array.from(new Set([...yearsFromActs, currentYear])).sort((a, b) => b - a);
        setAvailableYears(allYears);
        setLoadingYears(false);

        // Sum attendance hours for the month
        const attList = attendanceRes.data?.attendances || [];
        let totalAtt = 0;
        attList.forEach(att => {
          if (att.working_hours != null) totalAtt += parseFloat(att.working_hours);
        });
        setAttendanceHrs(totalAtt);

        // Assigned productivity — sum all days in the month
        const assignedList = assignedRes.data || [];
        if (assignedList.length > 0) {
          const totalAssigned = assignedList.reduce((acc, item) => {
            return acc + (item.hours != null ? parseFloat(item.hours) : 0);
          }, 0);
          setAssignedProductivityHrs(totalAssigned > 0 ? totalAssigned : null);
        } else {
          setAssignedProductivityHrs(null);
        }
      } catch (err) {
        console.error(err);
        setActivities([]);
        setAvailableYears([new Date().getFullYear()]);
        setLoadingYears(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [month, year, loggedUserId, targetEmployeeId]);

  // ── Computed summary values ─────────────────────────────────────────────────
  const totalActivityDecimal = activities.reduce(
    (acc, act) => acc + (parseFloat(act.duration_hours) || 0), 0
  );
  const totalActivityStr = formatDuration(totalActivityDecimal);
  const totalAttendanceStr = formatDuration(attendanceHrs);
  const assignedStr = assignedProductivityHrs !== null ? formatDuration(assignedProductivityHrs) : "--:--";

  // Productivity calculation: 3-factor formula
  const productivityPct = (() => {
    if (assignedProductivityHrs !== null && assignedProductivityHrs > 0) {
      const baseline = Math.max(assignedProductivityHrs, attendanceHrs);
      return baseline > 0 ? Math.min((totalActivityDecimal / baseline) * 100, 100) : null;
    }
    if (attendanceHrs > 0 && totalActivityDecimal > 0) {
      return Math.min((totalActivityDecimal / attendanceHrs) * 100, 100);
    }
    return null;
  })();

  const productivityStr = productivityPct !== null ? String(Math.round(productivityPct)) : "--";

  const hasAttendanceData = attendanceHrs > 0;
  const productivityColor =
    !hasAttendanceData && assignedProductivityHrs === null ? "text-white/40"
      : productivityPct === null ? "text-white/40"
        : productivityPct >= 80 ? "text-emerald-300"
          : productivityPct >= 50 ? "text-amber-300"
            : "text-red-400";

  const productivityBarColor =
    productivityPct === null ? "bg-white/20"
      : productivityPct >= 80 ? "bg-emerald-400"
        : productivityPct >= 50 ? "bg-amber-400"
          : "bg-red-400";

  const productivityLabel =
    productivityPct === null
      ? (assignedProductivityHrs === null ? "No Target Set" : "No Data")
      : productivityPct >= 80 ? "Excellent"
        : productivityPct >= 50 ? "Good"
          : "Low";

  // ── Excel download ──────────────────────────────────────────────────────────
  const downloadExcel = () => {
    if (!activities.length) return;

    const dateTotals = {};
    activities.forEach((act) => {
      const d = act.activity_date;
      const hrs = parseFloat(act.duration_hours || 0);
      dateTotals[d] = (dateTotals[d] || 0) + hrs;
    });

    const headers = ["Date", "Project", "Discipline", "Stage", "Description", "Duration", "Total Duration"];
    const rows = activities.map((act) => [
      act.activity_date,
      displayVal(act.project_name),
      displayVal(act.discipline_name),
      displayVal(act.stage_name),
      displayVal(act.description),
      formatDuration(act.duration_hours),
      formatDuration(dateTotals[act.activity_date]),
    ]);

    const spacingRow = ["", "", "", "", "", "", ""];
    const summaryRow = [
      "Total Activity Hrs", totalActivityStr,
      "Total Attendance Hrs", totalAttendanceStr,
      "Assigned Productivity", assignedStr,
      "Productivity", productivityStr !== "--" ? `${productivityStr}%` : "--",
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows, spacingRow, summaryRow]);
    ws["!cols"] = [
      { wch: 15 }, // Date
      { wch: 25 }, // Project
      { wch: 20 }, // Discipline
      { wch: 25 }, // Stage
      { wch: 40 }, // Description
      { wch: 15 }, // Duration
      { wch: 18 }, // Total Duration
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee Activity Report");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });
    const s2ab = (s) => {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };

    const blob = new Blob([s2ab(wbout)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const employeeId = useAuthStore.getState().employeeId || 'employee';
    link.setAttribute("download", `EmployeeProgress_${employeeId}_${month}_${year}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBack = () => navigate("/employee/assigned-projects");

  const LayoutWrapper = embedMode ? ({ children }) => <>{children}</> : EmployeeLayout;

  return (
    <LayoutWrapper>
      <div className="w-full max-w-[1100px] mx-auto p-3 sm:p-6">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full">

          {/* HEADER */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-xl sm:text-2xl">📊</span>
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-white">Employee Project Progress Report</h2>
                <p className="text-white/60 text-xs sm:text-sm">View your full project work progress</p>
              </div>
            </div>
            <button
              onClick={downloadExcel}
              disabled={activities.length === 0}
              className="self-start sm:self-auto px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold shadow transition"
            >
              ⬇ Download Report
            </button>
          </div>

          {/* FILTERS */}
          <div className="flex flex-wrap gap-3 justify-start sm:justify-end mb-6">
            <div className="flex flex-col text-sm">
              <label className="text-white mb-1">Month</label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="bg-black/40 border border-white/20 rounded-lg px-3 sm:px-4 py-2 text-white backdrop-blur-md appearance-none focus:outline-none focus:ring-2 focus:ring-white/30 text-sm min-w-[120px]"
              >
                {MONTHS.map((m) => (
                  <option key={m} value={m} className="bg-black text-white">{m}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col text-sm">
              <label className="text-white mb-1">Year</label>
              <select
                value={year || ""}
                onChange={(e) => setYear(Number(e.target.value))}
                disabled={loadingYears}
                className="bg-black/40 border border-white/20 rounded-lg px-3 sm:px-4 py-2 text-white backdrop-blur-md appearance-none focus:outline-none focus:ring-2 focus:ring-white/30 text-sm min-w-[90px]"
              >
                {loadingYears ? (
                  <option>Loading...</option>
                ) : (
                  availableYears.map((y) => (
                    <option key={y} value={y} className="bg-black text-white">{y}</option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* TABLE */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl w-full">
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm border-collapse min-w-[500px]">
                <thead className="bg-gradient-to-r from-slate-700 to-slate-800">
                  <tr>
                    <th className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Date</th>
                    <th className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Project</th>
                    <th className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Discipline</th>
                    <th className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Stage</th>
                    <th className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Description</th>
                    <th className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Duration</th>
                    <th className="px-3 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Total Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 sm:py-6 text-white/60 text-sm">
                        Loading report data...
                      </td>
                    </tr>
                  ) : activities.length > 0 ? (() => {
                    const dateRowSpans = [];
                    const dateTotalDurations = [];
                    let i = 0;
                    while (i < activities.length) {
                      let count = 1;
                      let totalHrs = parseFloat(activities[i].duration_hours || 0);
                      while (
                        i + count < activities.length &&
                        activities[i + count].activity_date === activities[i].activity_date
                      ) {
                        totalHrs += parseFloat(activities[i + count].duration_hours || 0);
                        count++;
                      }
                      dateRowSpans[i] = count;
                      dateTotalDurations[i] = totalHrs;
                      for (let j = 1; j < count; j++) {
                        dateRowSpans[i + j] = 0;
                        dateTotalDurations[i + j] = 0;
                      }
                      i += count;
                    }

                    return activities.map((act, idx) => {
                      const rowSpan = dateRowSpans[idx];
                      return (
                        <tr key={idx} className="text-center border-b border-white/5 hover:bg-white/5 transition animate-fade-in">
                          {rowSpan > 0 && (
                            <td
                              rowSpan={rowSpan}
                              className="px-3 sm:px-4 py-3 sm:py-4 text-white whitespace-nowrap align-middle border-r border-white/10 bg-white/5 font-medium"
                            >
                              {act.activity_date}
                            </td>
                          )}
                          <td className="px-3 sm:px-4 py-3 sm:py-4 text-white">{displayVal(act.project_name)}</td>
                          <td className="px-3 sm:px-4 py-3 sm:py-4 text-white">{displayVal(act.discipline_name)}</td>
                          <td className="px-3 sm:px-4 py-3 sm:py-4 text-white">{displayVal(act.stage_name)}</td>
                          <td className="px-3 sm:px-4 py-3 sm:py-4 text-white/70">{displayVal(act.description)}</td>
                          <td className="px-3 sm:px-4 py-3 sm:py-4 text-white whitespace-nowrap">{formatDuration(act.duration_hours)}</td>
                          {rowSpan > 0 && (
                            <td
                              rowSpan={rowSpan}
                              className="px-3 sm:px-4 py-3 sm:py-4 text-white whitespace-nowrap align-middle border-l border-white/10 bg-white/5 font-semibold"
                            >
                              {formatDuration(dateTotalDurations[idx])}
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })() : (
                    <tr>
                      <td colSpan="7" className="text-center py-8 sm:py-6 text-white/60 text-sm">
                        No activities found for this month/year.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── SUMMARY CARDS ── */}
            {!loading && activities.length > 0 && (
              <div className="mt-5 pt-4 border-t border-white/15 grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 pb-5">

                {/* Total Attendance Hrs */}
                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center">
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mb-1">Total Attendance Hrs</p>
                  <p className={`text-xl font-bold font-mono ${attendanceHrs > 0 ? "text-white" : "text-white/30"}`}>
                    {totalAttendanceStr !== "--" ? `${totalAttendanceStr} Hrs` : "00:00 Hrs"}
                  </p>
                  <p className="text-white/30 text-[10px] mt-0.5">for this month</p>
                </div>

                {/* Total Activity Hrs */}
                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center">
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mb-1">Total Activity Hrs</p>
                  <p className="text-white text-xl font-bold font-mono">
                    {totalActivityStr !== "--" ? `${totalActivityStr} Hrs` : "00:00 Hrs"}
                  </p>
                  <p className="text-white/30 text-[10px] mt-0.5">logged this month</p>
                </div>

                {/* Assigned Productivity */}
                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center">
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mb-1">Assigned Productivity</p>
                  <p className={`text-xl font-bold font-mono ${assignedProductivityHrs !== null ? "text-indigo-300" : "text-white/30"}`}>
                    {assignedProductivityHrs !== null ? `${assignedStr} Hrs` : "--:-- Hrs"}
                  </p>
                  <p className="text-white/30 text-[10px] mt-0.5">
                    {assignedProductivityHrs !== null ? "set by manager" : "not assigned yet"}
                  </p>
                </div>

                {/* Productivity */}
                <div className="col-span-2 lg:col-span-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3">
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mb-2 text-center">Productivity</p>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-2xl font-bold font-mono ${productivityColor}`}>
                      {productivityStr !== "--" ? `${productivityStr}%` : "—"}
                    </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${productivityPct === null
                      ? "bg-white/10 text-white/40"
                      : productivityPct >= 80 ? "bg-emerald-500/25 text-emerald-300"
                        : productivityPct >= 50 ? "bg-amber-500/25 text-amber-300"
                          : "bg-red-500/25 text-red-400"
                      }`}>
                      {productivityLabel}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${productivityBarColor}`}
                      style={{ width: `${productivityPct !== null ? productivityPct : 0}%` }}
                    />
                  </div>
                  <p className="text-white/30 text-[10px] mt-1 text-center">
                    {productivityPct !== null
                      ? assignedProductivityHrs !== null
                        ? `${totalActivityStr} activity ÷ ${formatDuration(Math.max(assignedProductivityHrs, attendanceHrs))} target`
                        : `${totalActivityStr} activity ÷ ${totalAttendanceStr} attendance`
                      : "No data to calculate productivity"}
                  </p>
                </div>

              </div>
            )}
          </div>

          {/* BACK BUTTON */}
          {!embedMode && (
            <div className="mt-6 flex justify-start">
              <button
                onClick={handleBack}
                className="px-5 sm:px-6 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold text-sm transition"
              >
                ← Back
              </button>
            </div>
          )}

        </motion.div>
      </div>
    </LayoutWrapper>
  );
}

export default EmployeeProjectProgress;
